/**
 * Back to top, pulled by the mascot.
 *
 * The robot walks in along the bottom of the screen, reaches for a thread
 * hanging from the top, and tugs — and the page runs back up as if the thread
 * were hauling it. Purely a flourish on the scroll: if the artwork has not
 * loaded in time the page just scrolls, and any wheel, touch or key press
 * from the reader stops the pull and hands the page back.
 *
 * The frames are a sprite sheet cut from the mascot clip (walk, then reach),
 * drawn by stepping `background-position`; no video element, so it plays the
 * same on every phone. The sheet ends at the robot's shins, which is why it
 * stands on the very bottom edge of the viewport.
 */

import sprite from '../../../../assets/elf/robo-pull-sprite.webp';

const COLS = 8;
const ROWS = 5;
/** Frames 0..WALK-1 walk; the rest raise the arm to the thread. */
const WALK = 27;
const FRAMES = 40;
const FRAME_ASPECT = 402 / 524;
/** Where the raised hand sits in the last frame, as a fraction of the frame. */
const HAND_X = 0.228;
const HAND_Y = 0.27;

const WALK_MS = 1150;
const REACH_MS = 560;
const TUG_MS = 220;
const TUG_PX = 26;
const LEAVE_MS = 380;
/** Longest the click waits on the artwork before scrolling without it. */
const ART_WAIT_MS = 1200;

let art: Promise<boolean> | null = null;
let running = false;

/** Fetch and decode the sprite ahead of the click. Safe to call repeatedly. */
export function warmTopPull(): Promise<boolean> {
  if (!art) {
    art = new Promise<boolean>((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => {
        art = null;
        resolve(false);
      };
      img.src = sprite;
    });
  }
  return art;
}

const easeOut = (t: number) => 1 - (1 - t) ** 3;
const easeInOut = (t: number) => (t < 0.5 ? 4 * t * t * t : 1 - (-2 * t + 2) ** 3 / 2);
const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

function plainScroll(): void {
  window.scrollTo({ top: 0, behavior: 'smooth' });
}

export async function pullToTop(): Promise<void> {
  if (running) return;
  // Nothing to haul: a robot for a few pixels of scroll would only be a delay.
  if (window.scrollY < window.innerHeight * 0.5) return plainScroll();

  running = true;
  const ready = await Promise.race([
    warmTopPull(),
    new Promise<boolean>((resolve) => window.setTimeout(() => resolve(false), ART_WAIT_MS)),
  ]);
  if (!ready) {
    running = false;
    return plainScroll();
  }

  const vw = window.innerWidth;
  const vh = window.innerHeight;
  // Sized to the screen's height, but never wider than half a narrow phone.
  const height = Math.min(clamp(vh * 0.42, 190, 440), Math.max(190, (vw * 0.5) / FRAME_ASPECT));
  const width = height * FRAME_ASPECT;
  const threadX = Math.min(vw * 0.5, 460);
  const restX = threadX - width * HAND_X;
  const startX = -width - 24;
  const handY = vh - height + height * HAND_Y;

  // Inside `.elf` so the palette and the stylesheet's scope both apply.
  const host = document.querySelector('.elf') ?? document.body;
  const layer = document.createElement('div');
  layer.className = 'elf-pull';
  layer.setAttribute('aria-hidden', 'true');
  const thread = document.createElement('div');
  thread.className = 'elf-pull__thread';
  thread.style.left = `${threadX}px`;
  thread.style.height = `${handY}px`;
  const robo = document.createElement('div');
  robo.className = 'elf-pull__robo';
  robo.style.width = `${width}px`;
  robo.style.height = `${height}px`;
  robo.style.backgroundImage = `url(${sprite})`;
  robo.style.backgroundSize = `${COLS * 100}% ${ROWS * 100}%`;
  layer.append(thread, robo);
  host.appendChild(layer);

  const show = (frame: number) => {
    const f = clamp(Math.floor(frame), 0, FRAMES - 1);
    robo.style.backgroundPosition = `${((f % COLS) / (COLS - 1)) * 100}% ${
      (Math.floor(f / COLS) / (ROWS - 1)) * 100
    }%`;
  };

  const from = window.scrollY;
  const scrollMs = clamp(from * 0.22, 800, 1600);
  const pullAt = WALK_MS + REACH_MS;
  const total = pullAt + Math.max(TUG_MS, scrollMs);
  const began = performance.now();
  let raf = 0;
  let done = false;

  const finish = () => {
    if (done) return;
    done = true;
    cancelAnimationFrame(raf);
    for (const type of STOPPERS) window.removeEventListener(type, finish);
    layer.classList.add('is-leaving');
    window.setTimeout(() => {
      layer.remove();
      running = false;
    }, LEAVE_MS);
  };
  // The reader taking over always wins.
  const STOPPERS = ['wheel', 'touchstart', 'keydown', 'pointerdown'] as const;
  for (const type of STOPPERS) window.addEventListener(type, finish, { passive: true });

  const tick = (now: number) => {
    const t = now - began;
    if (t < WALK_MS) {
      const p = t / WALK_MS;
      show(p * WALK);
      robo.style.transform = `translate3d(${startX + (restX - startX) * easeOut(p)}px, 0, 0)`;
    } else if (t < pullAt) {
      show(WALK + ((t - WALK_MS) / REACH_MS) * (FRAMES - WALK));
      robo.style.transform = `translate3d(${restX}px, 0, 0)`;
    } else {
      show(FRAMES - 1);
      const tug = TUG_PX * easeOut(clamp((t - pullAt) / TUG_MS, 0, 1));
      robo.style.transform = `translate3d(${restX}px, ${tug}px, 0)`;
      thread.style.height = `${handY + tug}px`;
      const p = clamp((t - pullAt) / scrollMs, 0, 1);
      window.scrollTo({ top: from * (1 - easeInOut(p)), behavior: 'instant' as ScrollBehavior });
    }
    if (t >= total) return finish();
    raf = requestAnimationFrame(tick);
  };
  show(0);
  robo.style.transform = `translate3d(${startX}px, 0, 0)`;
  raf = requestAnimationFrame(tick);
}
